import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import RadioButtonGroups from "@components/shared/FormElements/RadioButtonGroups/RadioButtonGroups";
import Loader from "@components/shared/Loader/Loader";
import { COUNTRIES, FILTER_TYPES } from "@constants/dashboard";
import { CONSTANTS } from "@constants/index";
import useLoadGoogleMaps from "@hooks/useLoadGoogleMaps/useLoadGoogleMaps";
import { useOrganisationFilter } from "@hooks/useOrganisationFilter/useOrganisationFilter";
import {
  LocationPositionType,
  OrganisationFilterProps,
} from "@models/components/organisation.model";
import { RootState } from "@store/index";
import {
  fetchCurrentLocationWithGoogleMap,
  isMobile,
} from "@utils/common.utils";

import SearchInput from "../SearchInput/SearchInput";

const OrganisationFilter: React.FC<OrganisationFilterProps> = ({
  closeModal,
  filterChange,
  organisationData,
}) => {
  const { t } = useTranslation();

  const modalRef = useRef<HTMLDivElement>(null);

  const isScriptLoaded = useLoadGoogleMaps();

  const [cityName, setCityName] = useState("");
  const [clubName, setClubName] = useState("");
  const [country, setCountry] = useState(COUNTRIES.SWITZERLAND);
  const [filterType, setFilterType] = useState(FILTER_TYPES.CITY);
  const [position, setPosition] = useState<LocationPositionType>({
    latitude: null,
    longitude: null,
  });
  const [loader, setLoader] = useState(false);
  const {
    filterTypeSetting,
    getDefaultBrowserLocation,
    handleSearch,
    isCity,
    resetSearch,
  } = useOrganisationFilter({
    cityName,
    clubName,
    country,
    filterChange,
    filterType,
    organisationData,
    setCityName,
    setClubName,
    setCountry,
    setFilterType,
  });

  const { country: location } = useSelector((state: RootState) => state.app);

  const {
    city: searchedCity,
    club: searchedClub,
    country: searchedCountryName,
    isSearch,
  } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        closeModal();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === CONSTANTS.KEYBOARD_KEYS.ENTER) {
        event.preventDefault();
        handleSearch(closeModal);
      }
    };
    document.addEventListener("keypress", handleKeyPress);
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [closeModal, handleSearch]);

  useEffect(() => {
    getDefaultBrowserLocation(setPosition, setLoader);
  }, []);

  useEffect(() => {
    if (!location && isScriptLoaded) {
      setLoader(true);
      if (!isSearch) {
        fetchCurrentLocationWithGoogleMap(position, setLoader, locationSetting);
      } else {
        fetchCurrentLocationWithGoogleMap(position, setLoader);
      }
    }
  }, [position, location, isScriptLoaded, isSearch]);

  useEffect(() => {
    if (isSearch) {
      setCountry(searchedCountryName);
      filterTypeSetting();
    }
  }, [searchedCountryName, searchedClub, searchedCity, isSearch]);

  const countryRadioGroup = [
    {
      id: "regionRadio1",
      label: t("switzerland"),
      name: country,
      setValue: setCountry,
      value: COUNTRIES.SWITZERLAND,
    },
    {
      id: "regionRadio2",
      label: t("germany"),
      name: country,
      setValue: setCountry,
      value: COUNTRIES.GERMANY,
    },
  ];

  const filterRadioGroup = [
    {
      id: "clubRadio1",
      label: t("cityName"),
      name: filterType,
      setValue: setFilterType,
      value: FILTER_TYPES.CITY,
    },
    {
      id: "clubRadio2",
      label: t("clubName"),
      name: filterType,
      setValue: setFilterType,
      value: FILTER_TYPES.CLUB,
    },
  ];

  const locationSetting = (city: string, countryCode: string) => {
    setCityName(city);
    if (
      countryCode === COUNTRIES.SWITZERLAND ||
      countryCode === COUNTRIES.GERMANY
    )
      setCountry(countryCode);
    else setCountry(COUNTRIES.SWITZERLAND);
  };

  const searchInputProps = {
    label: isCity ? t("cityName") : t("clubName"),
    setValue: isCity ? setCityName : setClubName,
    value: isCity ? cityName : clubName,
  };
  return (
    <div
      className={`fg-filter-popup show ${isMobile() ? "fg-mobile" : ""}`}
      data-testid="organisation-filter-test"
      ref={modalRef}
    >
      <div className="fg-filter-popup-header">
        <h5>{t("searchOrganizations")}</h5>
        <button className="btn fg-filter-close" onClick={closeModal}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <Loader isLoading={loader} />
      <div className="fg-filter-popup-body">
        <div className="fg-filter-popup-section">
          <p>{t("orChooseLocation")}</p>
          <RadioButtonGroups groups={countryRadioGroup} />
          <br />
          <RadioButtonGroups groups={filterRadioGroup} />
        </div>
        <div className="fg-filter-popup-section">
          <SearchInput {...searchInputProps} />
          <div className="row">
            <div className="col-6">
              <button
                type="button"
                className="btn fg-btn fg-btn-filled-darkblue w-100"
                onClick={() => handleSearch(closeModal)}
                data-testid="search-button"
              >
                {t("search")}
              </button>
            </div>
            <div className="col-6">
              <button
                className="btn fg-btn fg-btn-grey w-100"
                onClick={() => resetSearch(closeModal)}
                data-testid="reset-button"
              >
                {t("resetSearch")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganisationFilter;
